import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SectionCard from '../ui/SectionCard';
import { Card } from '../ui/Card';
import Button from '../ui/Button';
import PageHeader from '../ui/PageHeader';
import Slideover from '../ui/Slideover';
import { Input } from '../ui/Input';
import SelectMenu from '../ui/SelectMenu';
import { useConfirm } from '../ui/ConfirmationModal';
import { PlusIcon, MapIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import DropdownMenu from '../ui/DropdownMenu';
import StatusPill from '../ui/StatusPill';
import { VOCAB_SEEDS } from '../../constants/vocabSeeds';
import { mergeAreasWithTenant, relevantBuiltInAreas } from '../../quote-builder/areasModel';
import { settingsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../ui/Toast';
import { LABOR_METHODS, normaliseLaborMethod, isAutoCalculable } from '../../quote-builder/laborMethods';

/**
 * QuoteAreasPage — Settings surface for areas + sub-areas.
 *
 * R20-15/R20-16 (2026-08-06): the BIDIT lock is retired. All areas and
 * sub-areas are editable (rename) and deactivatable (soft-delete).
 * Custom top-level areas are supported via list_type='custom_areas'.
 *
 * Deactivate (not hard delete) per Lindsey DM-361: "They can deactivate
 * them. We don't ever delete." A deactivated sub-area disappears from
 * pickers but keeps its name resolvable for historical quotes.
 */

const BASE_AREAS = VOCAB_SEEDS.area?.values || [];

// The settings Lambda answers 404 / "Item not found" when update_list_item
// targets a row that does not exist yet (e.g. the first override of a
// built-in area). Callers then fall back to add_list_item at the same id.
function isNotFoundError(err) {
  const status = err && (err.status || err.statusCode || err.response?.status);
  const msg = err && err.message ? String(err.message).toLowerCase() : '';
  return status === 404 || msg.includes('not found') || msg.includes('404');
}

const isInactive = (row) => Boolean(row) && row.active === false;

// ─── ⋮ row menu (IDI-448) ────────────────────────────────────────────────────
// The shared DropdownMenu overflow pattern (UsersRolesPage RowMenu): Edit, and
// Make inactive on an active row / Reactivate on an inactive one. Delete is
// out of scope for soft launch (ticket item 4).
export function AreaRowMenu({ kind, row, onEdit, onSetActive }) {
  const inactive = isInactive(row);
  const items = [
    { label: 'Edit', onClick: () => onEdit(row) },
    inactive
      ? { label: 'Reactivate', onClick: () => onSetActive(row, true) }
      : { label: 'Make inactive', variant: 'danger', onClick: () => onSetActive(row, false) },
  ];
  return (
    <DropdownMenu
      items={items}
      ariaLabel={`Actions for ${kind} ${row.label}`}
    />
  );
}

// ─── Sub-area Slideover (B3: add + edit, with labor fields) ─────────────────

// Common unit suffixes for labor rates. The suffix belongs to the RATE,
// not the method — a piece rate can be $/sq ft, $/board-ft, $/each, etc.
const LABOR_UNITS = [
  { value: '/ sq ft', label: '/ sq ft' },
  { value: '/ board-ft', label: '/ board-ft' },
  { value: '/ linear ft', label: '/ linear ft' },
  { value: '/ hr', label: '/ hr' },
  { value: '/ day', label: '/ day' },
  { value: '/ each', label: '/ each' },
];

function SubAreaSlideover({ open, area, subArea, onClose, onSaved }) {
  const { user } = useAuth();
  const toast = useToast();
  const isEdit = Boolean(subArea);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [laborMethod, setLaborMethod] = useState('');
  const [laborRate, setLaborRate] = useState('');
  const [laborUnit, setLaborUnit] = useState('');
  const [inheritsDefault, setInheritsDefault] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setSaving(false);
      if (subArea) {
        setName(subArea.label || subArea.name || '');
        setDescription(subArea.description || '');
        setLaborMethod(normaliseLaborMethod(subArea.laborMethod) || '');
        setLaborRate(subArea.laborRate != null ? String(subArea.laborRate) : '');
        setLaborUnit(subArea.laborUnit || '');
        setInheritsDefault(subArea.inheritsDefault !== false);
      } else {
        setName('');
        setDescription('');
        setLaborMethod('');
        setLaborRate('');
        setLaborUnit('');
        setInheritsDefault(true);
      }
    }
  }, [open, subArea]);

  const canSave = name.trim().length > 0 && !saving && Boolean(area);

  const handleResetToDefault = () => {
    setLaborMethod('');
    setLaborRate('');
    setLaborUnit('');
    setInheritsDefault(true);
  };

  const handleLaborChange = (method) => {
    setLaborMethod(method);
    if (method) setInheritsDefault(false);
  };

  const handleSave = useCallback(async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        laborMethod: laborMethod || null,
        laborRate: laborRate !== '' ? Number(laborRate) : null,
        laborUnit: laborUnit || null,
        inheritsDefault,
      };
      if (isEdit) {
        // PR-67a (2026-08-22): BIDIT sub-area rename repair.
        //
        // Before: `updateListItem('sub_areas', subArea.id, payload)` would
        // 404 whenever `subArea.id` was a BIDIT seed sub-area's `value`
        // (e.g. 'bay') because no row exists in settings.sub_areas for
        // that id — the seed only lives in vocabSeeds. Rename “vanished on
        // reload” (PORTICA-71) because the write failed with a toast the
        // user often missed.
        //
        // After: upsert. Try updateListItem first (works for tenant-added
        // sub-areas whose id is a real backend UUID, AND for BIDIT-seed
        // sub-areas that have already been renamed once and therefore have
        // an upsert row). If the row doesn't exist yet, fall back to
        // addListItem with the seed's id as the row id. The extended
        // mergeAreasWithTenant (areasModel.js) treats such rows as label
        // overrides for the matching seed sub-area, not as duplicates.
        //
        // Detection is by error shape: 404 (status code) or a message that
        // includes "not found". Any other error surfaces to the toast as
        // before.
        // IDI-662 D3: the Lambda upserts sub_areas and stamps active:true
        // when absent, so a bare rename stored a parentless (dropped by the
        // merge) or re-activated row. Send the parent and current state.
        const keep = {
          parent_area_id: area.value || area.id,
          active: subArea.active !== false,
        };
        try {
          await settingsAPI.updateListItem('sub_areas', subArea.id, { ...payload, ...keep });
        } catch (updateErr) {
          const status = updateErr && (updateErr.status || updateErr.statusCode);
          const msg = updateErr && updateErr.message ? String(updateErr.message).toLowerCase() : '';
          const isNotFound = status === 404 || msg.includes('not found') || msg.includes('404');
          if (!isNotFound) throw updateErr;
          // First rename of a BIDIT seed sub-area — create the upsert row.
          await settingsAPI.addListItem('sub_areas', {
            ...payload,
            id: subArea.id,
            ...keep,
            added_by: user?.id,
          });
        }
        toast?.success?.(`Sub-area "${name.trim()}" updated.`);
      } else {
        await settingsAPI.addListItem('sub_areas', {
          ...payload,
          parent_area_id: area.value || area.id,
          added_by: user?.id,
          active: true,
        });
        toast?.success?.(`Sub-area "${name.trim()}" added.`);
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast?.error?.(err?.message || `Failed to ${isEdit ? 'update' : 'add'} sub-area.`);
      setSaving(false);
    }
  }, [canSave, name, description, laborMethod, laborRate, laborUnit, inheritsDefault, isEdit, subArea, area, user, toast, onSaved, onClose]);

  return (
    <Slideover
      open={open}
      onClose={onClose}
      title={isEdit ? `Edit sub-area` : `Add sub-area under ${area?.label || area?.name || ''}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={!canSave}>{saving ? 'Saving…' : (isEdit ? 'Save' : 'Add')}</Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="Sub-area name *"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && canSave) { e.preventDefault(); handleSave(); } }}
          placeholder="e.g., Attic knee walls"
          autoFocus
        />
        <Input
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional"
        />

        {/* Labor cost type */}
        <div>
          <label className="block text-xs font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
            Labor cost type
          </label>
          <SelectMenu
            value={laborMethod}
            onChange={handleLaborChange}
            options={[{ value: '', label: 'None (inherit default)' }, ...LABOR_METHODS.map((m) => ({ value: m.id, label: m.label, hint: m.autoCalculable ? undefined : 'Coming soon', disabled: !m.autoCalculable }))]}
            width="full"
          />
        </div>

        {/* Amount + unit */}
        {laborMethod && (
          <div className="flex items-end gap-2">
            <Input
              label="Rate"
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              value={laborRate}
              onChange={(e) => setLaborRate(e.target.value)}
              placeholder="0.00"
              className="w-28"
            />
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Unit
              </label>
              <SelectMenu
                value={laborUnit}
                onChange={setLaborUnit}
                options={[{ value: '', label: 'Select unit' }, ...LABOR_UNITS.map((u) => ({ value: u.value, label: u.label }))]}
              />
            </div>
          </div>
        )}

        {/* Inherited / Overridden state */}
        <div className="text-xs pt-1" style={{ color: 'var(--color-text-muted)' }}>
          {inheritsDefault ? (
            <span>ℹ️ Inherited from Margins & Labor defaults</span>
          ) : (
            <span>
              Overridden —{' '}
              <button
                type="button"
                onClick={handleResetToDefault}
                className="underline"
                style={{ color: 'var(--color-action-primary)' }}
              >
                Reset to default
              </button>
            </span>
          )}
        </div>
      </div>
    </Slideover>
  );
}

// ─── Inline editable label ──────────────────────────────────────────────────
// IDI-448: optionally controlled (`editing` + `onEditingChange`) so the ⋮
// menu's Edit can open the inline rename.
function EditableLabel({ value, onSave, className = '', editing: editingProp, onEditingChange }) {
  const [editingState, setEditingState] = useState(false);
  const controlled = editingProp !== undefined;
  const editing = controlled ? editingProp : editingState;
  const setEditing = (next) => {
    if (!controlled) setEditingState(next);
    if (onEditingChange) onEditingChange(next);
  };
  const [draft, setDraft] = useState(value);

  useEffect(() => { setDraft(value); }, [value]);

  if (!editing) {
    return (
      <span className={`inline-flex items-center gap-1 group ${className}`}>
        <span>{value}</span>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-gray-200 transition-opacity"
          title="Edit name"
          aria-label={`Edit ${value}`}
        >
          <PencilSquareIcon className="h-3.5 w-3.5 text-gray-400" />
        </button>
      </span>
    );
  }

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value) {
      onSave(trimmed);
    }
    setEditing(false);
  };

  return (
    <input
      type="text"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); commit(); }
        if (e.key === 'Escape') { setDraft(value); setEditing(false); }
      }}
      className="text-sm font-medium border border-gray-300 rounded px-1.5 py-0.5 focus:outline-none focus:ring-2 focus:ring-[var(--color-brand)]"
      style={{ width: `${Math.max(draft.length, 8)}ch` }}
      aria-label={`Area name for ${value}`}
      autoFocus
    />
  );
}

// ─── QuoteAreasPage ─────────────────────────────────────────────────────────
export default function QuoteAreasPage() {
  const confirm = useConfirm();
  const toast = useToast();
  const [tenantSubAreas, setTenantSubAreas] = useState([]);
  const [customAreas, setCustomAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpenForArea, setAddOpenForArea] = useState(null);
  const [editSubArea, setEditSubArea] = useState(null); // { sub, parentArea }
  const [addAreaOpen, setAddAreaOpen] = useState(false);
  // IDI-448: inactive rows hidden by default (IDI-294 / Crews IDI-402 pattern).
  const [showInactive, setShowInactive] = useState(false);
  const [renamingAreaId, setRenamingAreaId] = useState(null);
  const { user } = useAuth();

  const refetch = useCallback(async () => {
    try {
      const [subs, areas] = await Promise.all([
        settingsAPI.getList('sub_areas'),
        settingsAPI.getList('custom_areas'),
      ]);
      setTenantSubAreas(Array.isArray(subs) ? subs : []);
      setCustomAreas(Array.isArray(areas) ? areas : []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refetch(); }, [refetch]);

  // Merged render shape.
  //
  // PR-67a (2026-08-22): `customAreas` is now passed as the third argument
  // to mergeAreasWithTenant so id-matching rows become label overrides for
  // BIDIT seed areas rather than duplicate entries. This is the fix for
  // PORTICA-71 on the read side: after a BIDIT-area rename lands in
  // settings.custom_areas at the seed's id, the next reload's merge
  // renders the tenant's name for that seed area, not the seed default.
  //
  // Non-matching custom_areas rows (tenant-created top-level areas via
  // handleCreateCustomArea at :~473) still appear as new top-level areas.
  //
  // The old `[...bidit, ...custom]` concat is retired: it produced two
  // top-level entries for the same id ("Ceilings" and "Ceilings") after
  // a BIDIT-area rename, which is the surface bug PORTICA-71 reported.
  //
  // IDI-405 (reopened): PR #462's blanket suppression hid built-in areas
  // that have sub-areas under them (Ceilings, Walls, Floors, Options for
  // Sample Dealer). Smarter guard: suppress a built-in area only if the
  // tenant has custom_areas AND the built-in has NO sub-areas parented to
  // it and NO custom_areas override for it. Built-ins that the tenant
  // actively uses (has sub-areas under) are kept.
  // IDI-662 D1: the guard applies to MIGRATED dealers only (legacy-origin
  // sub-areas); a clean dealer keeps every built-in. Shared with
  // AddItemCascade -- see areasModel.relevantBuiltInAreas.
  const relevantBaseAreas = useMemo(
    () => relevantBuiltInAreas(BASE_AREAS, tenantSubAreas, customAreas),
    [customAreas, tenantSubAreas]
  );

  // IDI-448: Settings merges WITH inactive rows (stamped active:false) and
  // filters for display; Add Item uses the default merge, which never offers
  // them.
  const allAreas = useMemo(
    () => mergeAreasWithTenant(
      relevantBaseAreas,
      tenantSubAreas,
      customAreas,
      { includeInactive: true },
    ),
    [relevantBaseAreas, tenantSubAreas, customAreas]
  );
  const inactiveCount = useMemo(
    () => allAreas.reduce(
      (n, a) => n + (isInactive(a) ? 1 : 0) + a.subAreas.filter(isInactive).length,
      0
    ),
    [allAreas]
  );
  // Toggle off: an inactive area is hidden whole (with its sub-areas), and
  // inactive sub-areas of active areas are hidden.
  const mergedAreas = useMemo(
    () => (showInactive
      ? allAreas
      : allAreas
        .filter((a) => !isInactive(a))
        .map((a) => ({ ...a, subAreas: a.subAreas.filter((sub) => !isInactive(sub)) }))),
    [allAreas, showInactive]
  );

  // ─── Handlers ───────────────────────────────────────────────────────────────

  // IDI-663 D2: one active area per name (trimmed, any case), built-in or
  // added, as listed under the dealer's labels (allAreas -- never the seeds).
  const findActiveAreaNamed = useCallback((name, exceptId) => {
    const want = (name || '').trim().toLowerCase();
    if (!want) return null;
    return allAreas.find((a) => !isInactive(a)
      && a.id !== exceptId
      && (a.label || '').trim().toLowerCase() === want) || null;
  }, [allAreas]);

  // IDI-448: write a patch onto an area's custom_areas row. Tenant areas
  // (and built-ins already overridden once) update in place; the first
  // override of a built-in is added at the seed's id, which the merge reads
  // as a label/active override (PORTICA-71). Updating first means a second
  // rename no longer appends a duplicate override row.
  const upsertArea = useCallback(async (area, patch) => {
    if (area.source === 'tenant') {
      await settingsAPI.updateListItem('custom_areas', area.id, patch);
      return;
    }
    try {
      await settingsAPI.updateListItem('custom_areas', area.id, patch);
    } catch (err) {
      if (!isNotFoundError(err)) throw err;
      await settingsAPI.addListItem('custom_areas', {
        id: area.id,
        isBiditOverride: true,
        originalLabel: area.originalLabel || area.label,
        ...patch,
      });
    }
  }, []);

  const handleRenameArea = useCallback(async (area, newName) => {
    if (!isInactive(area)) {
      const clash = findActiveAreaNamed(newName, area.id);
      if (clash) {
        toast.error(`An active area named "${clash.label}" already exists.`);
        return;
      }
    }
    try {
      await upsertArea(area, { name: newName });
      toast.success(`Area renamed to "${newName}"`);
      refetch();
    } catch (err) { toast.error('Failed to rename area'); }
  }, [findActiveAreaNamed, upsertArea, toast, refetch]);

  // IDI-448: Make inactive / Reactivate an area. Existing quote lines keep
  // their stored area name either way.
  const handleSetAreaActive = useCallback(async (area, active) => {
    if (!active) {
      const ok = await confirm({
        title: 'Make area inactive?',
        message: `Make "${area.label}" inactive? It and its sub-areas will no longer be offered in Add Item. Existing quotes keep their area and sub-area names. You can reactivate it from Show inactive.`,
        confirmLabel: 'Make inactive',
        tone: 'danger',
      });
      if (!ok) return;
    } else {
      const clash = findActiveAreaNamed(area.label, area.id);
      if (clash) {
        toast.error(`An active area named "${clash.label}" already exists. Rename one first.`);
        return;
      }
    }
    try {
      await upsertArea(area, { active });
      toast.success(active ? `Area "${area.label}" reactivated.` : `Area "${area.label}" is now inactive.`);
      refetch();
    } catch (err) {
      toast.error(active ? 'Failed to reactivate area' : 'Failed to make area inactive');
    }
  }, [confirm, findActiveAreaNamed, upsertArea, toast, refetch]);

  // B4: sub-area rename is handled via the side sheet (B3).

  // IDI-448: Make inactive / Reactivate a sub-area. parent_area_id is always
  // sent: for a built-in seed sub-area (or a migrated row the Lambda has not
  // seen an override for) the write lands as a NEW override row, and the
  // merge drops a sub_areas row with no parent -- the old deactivate sent
  // only {active:false}, so a seed sub-area never actually went inactive.
  const handleSetSubAreaActive = useCallback(async (sub, area, active) => {
    if (!active) {
      const ok = await confirm({
        title: 'Make sub-area inactive?',
        message: `Make "${sub.label}" in ${area.label} inactive? It will no longer be offered in Add Item. Existing quotes keep its name. You can reactivate it from Show inactive.`,
        confirmLabel: 'Make inactive',
        tone: 'danger',
      });
      if (!ok) return;
    }
    const patch = { active, parent_area_id: area.id };
    try {
      try {
        await settingsAPI.updateListItem('sub_areas', sub.id, patch);
      } catch (err) {
        if (!isNotFoundError(err)) throw err;
        await settingsAPI.addListItem('sub_areas', { id: sub.id, ...patch, added_by: user?.id });
      }
      toast.success(active ? `Sub-area "${sub.label}" reactivated.` : `Sub-area "${sub.label}" is now inactive.`);
      refetch();
    } catch (err) {
      toast.error(active ? 'Failed to reactivate sub-area' : 'Failed to make sub-area inactive');
    }
  }, [confirm, user, toast, refetch]);

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="px-6">
      <PageHeader
        title="Quote Areas"
        description="Areas and sub-areas used in the Quote Builder. Add custom areas or sub-areas to match your workflow."
        actions={
          <Button onClick={() => setAddAreaOpen(true)}>
            <PlusIcon className="h-4 w-4 mr-1" /> Add Area
          </Button>
        }
      />

      {/* IDI-448: show-inactive toggle, the Crews (IDI-402) control. */}
      {inactiveCount > 0 && (
        <div className="flex items-center gap-2 mb-3">
          <button
            type="button"
            onClick={() => setShowInactive(!showInactive)}
            aria-pressed={showInactive}
            className="text-xs px-2.5 py-1 rounded-full border transition-colors"
            style={{
              borderColor: showInactive ? 'var(--color-action-primary)' : 'var(--color-border)',
              color: showInactive ? 'var(--color-action-primary)' : 'var(--color-text-muted)',
              backgroundColor: showInactive ? 'var(--color-action-primary-light, rgba(215,3,33,0.06))' : 'transparent',
            }}
          >
            {showInactive ? 'Hide' : 'Show'} inactive ({inactiveCount})
          </button>
        </div>
      )}

      <div className="space-y-4">
        {mergedAreas.map((area) => (
          <SectionCard
            key={area.id}
            title={
              <span className="inline-flex items-center gap-2" style={isInactive(area) ? { opacity: 0.6 } : undefined}>
                <EditableLabel
                  value={area.label}
                  onSave={(newName) => handleRenameArea(area, newName)}
                  editing={renamingAreaId === area.id}
                  onEditingChange={(next) => setRenamingAreaId(next ? area.id : null)}
                />
                {isInactive(area) && <StatusPill status="inactive" size="sm">Inactive</StatusPill>}
              </span>
            }
            icon={MapIcon}
            actions={
              <div className="flex items-center gap-1">
                {!isInactive(area) && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setAddOpenForArea({ value: area.id, label: area.label, name: area.label })}
                  >
                    <PlusIcon className="h-4 w-4 mr-1" aria-hidden="true" />
                    Add sub-area
                  </Button>
                )}
                <AreaRowMenu
                  kind="area"
                  row={area}
                  onEdit={() => setRenamingAreaId(area.id)}
                  onSetActive={handleSetAreaActive}
                />
              </div>
            }
          >
            {area.subAreas.length === 0 ? (
              <p className="text-sm italic text-gray-500">
                No sub-areas yet. Click <em>Add sub-area</em> to create the first one under {area.label}.
              </p>
            ) : (
              <div className="space-y-2">
                {area.subAreas.map((sub) => {
                  const method = LABOR_METHODS.find((m) => m.id === normaliseLaborMethod(sub.laborMethod));
                  return (
                    <Card key={sub.id} className="p-3 group" data-testid={`subarea-${sub.id}`}>
                      <div className="flex items-start justify-between" style={isInactive(sub) ? { opacity: 0.6 } : undefined}>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                            <span>{sub.label}</span>
                            {isInactive(sub) && <StatusPill status="inactive" size="sm">Inactive</StatusPill>}
                          </p>
                          {sub.description && (
                            <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
                              {sub.description}
                            </p>
                          )}
                          <p className="text-xs mt-1" style={{ color: sub.inheritsDefault ? 'var(--color-text-muted)' : 'var(--color-text-secondary)' }}>
                            {sub.inheritsDefault || !sub.laborMethod
                              ? 'Inherited'
                              : `${method?.label || sub.laborMethod} · $${sub.laborRate ?? "—"} ${sub.laborUnit || ""}${!isAutoCalculable(normaliseLaborMethod(sub.laborMethod)) ? " (not yet applied)" : ""}`
                            }
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <AreaRowMenu
                            kind="sub-area"
                            row={sub}
                            onEdit={() => setEditSubArea({ sub, parentArea: { value: area.id, label: area.label } })}
                            onSetActive={(row, active) => handleSetSubAreaActive(row, area, active)}
                          />
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </SectionCard>
        ))}

        {loading && (
          <p className="text-sm italic text-gray-500 pl-1">Loading areas…</p>
        )}
      </div>

      <SubAreaSlideover
        open={addOpenForArea != null || editSubArea != null}
        area={addOpenForArea || editSubArea?.parentArea}
        subArea={editSubArea?.sub}
        onClose={() => { setAddOpenForArea(null); setEditSubArea(null); }}
        onSaved={refetch}
      />

      {/* Add Area slideover */}
      <Slideover
        open={addAreaOpen}
        onClose={() => setAddAreaOpen(false)}
        title="Add Area"
        footer={
          <>
            <Button variant="secondary" onClick={() => setAddAreaOpen(false)}>Cancel</Button>
            <Button onClick={async () => {
              const el = document.getElementById('new-area-name');
              const name = el?.value?.trim();
              if (!name) return;
              const clash = findActiveAreaNamed(name);
              if (clash) {
                toast.error(`An active area named "${clash.label}" already exists.`);
                return;
              }
              try {
                await settingsAPI.addListItem('custom_areas', { id: `area-${Date.now()}`, name, subAreas: [] });
                toast.success('Area added');
                setAddAreaOpen(false);
                refetch();
              } catch (err) { toast.error('Failed to add area'); }
            }}>Add</Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input id="new-area-name" label="Area Name *" placeholder="e.g., Garage, Basement" autoFocus />
        </div>
      </Slideover>
    </div>
  );
}
